import { Trans } from "@lingui/react/macro";
import {
  ClockArrowDownIcon,
  ClockArrowUpIcon,
  MoreHorizontalIcon,
  XIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  ChatOrderDirection as OrderDirection,
  ChatOrderKey as OrderKey,
} from "@/lib/ai/ai-chat-ui-context";
import { cn } from "@/lib/utils";

type Props = {
  orderKey: OrderKey;
  orderDirection: OrderDirection;
  includesShared: boolean;
  onToggleDirection: () => void;
  onOrderKeyChange: (key: OrderKey) => void;
  onIncludesSharedChange: (checked: boolean) => void;
  className?: string;
  onClose?: () => void;
};

/**
 *　チャット履歴一覧のソート・フィルタを管理するUI（ヘッダー）
 */
export function ChatListControls({
  orderKey,
  orderDirection,
  includesShared,
  onToggleDirection,
  onOrderKeyChange,
  onIncludesSharedChange,
  className,
  onClose,
}: Props) {
  return (
    <div
      className={cn(
        `flex items-center justify-between border-b px-4 h-11 shrink-0`,
        className,
      )}
    >
      <span className="text-sm font-semibold text-foreground">
        <Trans>Chats</Trans>
      </span>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon-sm"
              variant="ghost"
              onClick={onToggleDirection}
              aria-label="toggle-order-direction"
            >
              {orderDirection === "ASC" ? (
                <ClockArrowUpIcon className="h-4 w-4" />
              ) : (
                <ClockArrowDownIcon className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {orderDirection === "ASC" ? (
              <Trans>Ascending</Trans>
            ) : (
              <Trans>Descending</Trans>
            )}
          </TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon-sm" variant="ghost" aria-label="chat-filters">
              <MoreHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={6}>
            <DropdownMenuLabel>
              <Trans>Order by</Trans>
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={orderKey}
              onValueChange={(value) => onOrderKeyChange(value as OrderKey)}
            >
              <DropdownMenuRadioItem value="UPDATED_AT">
                <Trans>Last updated</Trans>
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="CREATED_AT">
                <Trans>Created</Trans>
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={includesShared}
              onCheckedChange={(value) =>
                onIncludesSharedChange(value === true)
              }
            >
              <Trans>Include shared chats</Trans>
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {onClose && (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="close"
            onClick={onClose}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
